/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-08-2026
 *
 * Het docentendashboard (golf 3f): een overzicht van wat de studenten in dit project doen, als
 * gestapelde balkgrafiek per model met een donut voor de verdeling over het geheel. Vervangt de
 * `dashboard`-directive uit ng1.
 *
 * De binnenkomende statistiek en de open-state komen uit de `Dashboardv1Service`; alles wat je
 * hier kunt instellen — het filter live/alles, welke balken en series je ziet, en welk model je
 * in detail bekijkt — is state van dit paneel en leeft dus hier. De telling is een berekening
 * over die twee: één computed die opnieuw draait als de service publiceert of als je iets
 * aanklikt.
 *
 * De grafieken zelf staan in een child achter een `@defer`, zodat Chart.js pas laadt als het
 * dashboard voor het eerst opengaat.
 */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    type OnDestroy,
    signal
} from '@angular/core';
import type {ChartDataset, ChartOptions} from 'chart.js';
import {TranslatePipe} from '@ngx-translate/core';
import {BaseIslandComponent} from '../base-island.component';
import {Dashboardv1Service} from '../../services/dashboardv1.service';
import {SessieService} from '../../services/sessie.service';
import {ModelbeheerService} from '../../app/model/modelbeheer.service';
import {DlDashboardv1GrafiekenComponent} from './dl-dashboardv1-grafieken.component';
import {BALKEN, type BalkKey, RANDKLEUR, type Serie, SERIES, standaardVerborgen} from './dashboardv1-series';
import {bouwTelling} from './dashboardv1-telling';

/**
 * De breedte die één model in de grafiek krijgt. Bepaalt zowel de maximale dikte van een balkje
 * als de totale breedte van het scrollvlak.
 */
const BALKBREEDTE = 40;

/**
 * De kleur waarin een gewoon x-label getekend wordt; de Chart.js-standaard, hier expliciet
 * omdat het label van een live model juist rood moet worden.
 */
const LABELKLEUR = '#666';

@Component({
    selector: 'dl-dashboardv1',
    standalone: true,
    templateUrl: './dl-dashboardv1.component.html',
    styleUrls: ['./dl-dashboardv1.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DlDashboardv1GrafiekenComponent, TranslatePipe],
    host: {
        '[class.dl-dashboardv1--open]': 'dashboard.open()'
    }
})
export class DlDashboardv1Component extends BaseIslandComponent implements OnDestroy {

    /**
     * De sessie (golf a1): of de gebruiker met een model mag meekijken — dat bepaalt of een klik
     * op het detailplaatje ergens toe leidt — en zijn weergavenaam. Het backend zet in de
     * modelgegevens dezelfde uitdrukking (`displaynaam || username`), dus aan die naam is te zien
     * welke modellen van de gebruiker zelf zijn; die open je gewoon in plaats van dat je ermee
     * meekijkt.
     */
    private readonly sessie = inject(SessieService);

    /**
     * De open-state en de binnenkomende statistiek.
     */
    protected readonly dashboard = inject(Dashboardv1Service);

    /**
     * Het modelbeheer, dat een model uit het detailoverzicht opent (golf 4c).
     */
    private readonly modelbeheer = inject(ModelbeheerService);

    /**
     * Welke modellen meetellen: alleen de modellen die op dit moment openstaan, of alle.
     * Begint op "alles" — ng1 begon op localhost op "live", een ontwikkelconditie die met de
     * migratie vervalt (besluit B).
     */
    protected readonly stand = signal<'live' | 'alles'>('alles');

    /**
     * Welke balken (en daarmee welke y-assen) getoond worden.
     */
    protected readonly balkAan = signal<Record<BalkKey, boolean>>({acties: true, elementen: true, norm: true});

    /**
     * De series die de gebruiker heeft uitgezet.
     */
    protected readonly verborgen = signal<ReadonlySet<string>>(standaardVerborgen());

    /**
     * Het model waarvan we het detail bekijken, of `null` voor het overzicht.
     */
    protected readonly detail = signal<string | null>(null);

    /**
     * De balken met hun aan/uit-knop.
     */
    protected readonly balken = BALKEN;

    /**
     * De telling over de laatst gepubliceerde gegevens, met het huidige filter.
     */
    protected readonly telling = computed(() =>
        bouwTelling(this.dashboard.gegevens(), this.detail(), this.stand() === 'live'));

    /**
     * De series die een eigen knopje krijgen, per balk. Series die een andere volgen gaan met
     * dat knopje mee en krijgen er zelf geen.
     */
    protected readonly knoppenPerBalk = BALKEN.map(balk => ({
        balk: balk.key,
        series: SERIES.filter(serie => serie.balk === balk.key && !serie.volgt)
    }));

    /**
     * De labels onder de balken.
     */
    protected readonly barLabels = computed(() => this.telling().labels);

    /**
     * De gestapelde datasets: per serie met data één rij getallen over de getoonde modellen.
     */
    protected readonly barDatasets = computed<ChartDataset<'bar'>[]>(() => {
        const telling = this.telling();
        return telling.series.map(serie => ({
            label: serie.label,
            data: telling.chartmodels.map(modelId => telling.perModel[modelId][serie.seriekey] ?? 0),
            backgroundColor: serie.kleur,
            borderColor: RANDKLEUR[serie.balk],
            borderWidth: 2,
            maxBarThickness: BALKBREEDTE,
            minBarLength: 0.5,
            barPercentage: 0.8,
            categoryPercentage: 0.9,
            stack: serie.balk,
            yAxisID: serie.balk,
            hidden: !this.isZichtbaar(serie)
        }));
    });

    /**
     * De series in de donut: alles wat zichtbaar is en niet uitgesloten (de normseries tellen
     * niet mee — die zeggen iets over de stand van een model, niet over wat er gedaan is).
     */
    private readonly donutSeries = computed(() =>
        SERIES.filter(serie => this.isZichtbaar(serie) && !serie.nietInDonut));

    /**
     * De labels van de donutpunten.
     */
    protected readonly donutLabels = computed(() => this.donutSeries().map(serie => serie.label));

    /**
     * De ene donutdataset, met per punt zijn eigen kleur.
     */
    protected readonly donutDatasets = computed<ChartDataset<'doughnut'>[]>(() => {
        const totalen = this.telling().totalen;
        return [{
            data: this.donutSeries().map(serie => totalen[serie.seriekey] ?? 0),
            backgroundColor: this.donutSeries().map(serie => serie.kleur)
        }];
    });

    /**
     * De breedte die de balkgrafiek nodig heeft; ruim, want hij komt in een scroller.
     */
    protected readonly breedte = computed(() => 400 + this.telling().labels.length * (BALKBREEDTE + 10));

    /**
     * Of er iets te tekenen valt: zonder modellen of met alles uitgezet blijven de grafieken weg.
     */
    protected readonly dataLeeg = computed(() => {
        const telling = this.telling();
        return telling.chartmodels.length === 0 || telling.series.every(serie => !this.isZichtbaar(serie));
    });

    /**
     * De naam van het model in detailmodus.
     */
    protected readonly detailTitel = computed(() => {
        const info = this.dashboard.gegevens().modelinfo[this.detail() ?? ''];
        return info ? `${info.user}: ${info.titel}` : '';
    });

    /**
     * De url van het modelplaatje in detailmodus. Het publicatienummer erachter zorgt dat een
     * vers plaatje ook echt opgehaald wordt.
     */
    protected readonly detailImg = computed(() => {
        const gegevens = this.dashboard.gegevens();
        const url = gegevens.modelinfo[this.detail() ?? '']?.imgUrl;
        return url ? `${url}?${gegevens.publicatie}` : null;
    });

    /**
     * Of de gebruiker met een model mag meekijken.
     */
    protected readonly magMeekijkenAan = this.sessie.magMeekijken;

    /**
     * Of het model in detailmodus van de gebruiker zelf is.
     */
    private readonly isEigenModel = computed(() => {
        const naam = this.sessie.displaynaam();
        const info = this.dashboard.gegevens().modelinfo[this.detail() ?? ''];
        return !!naam && info?.user === naam;
    });

    /**
     * De opties van de balkgrafiek. Eén keer opgebouwd: de callbacks lezen bij het tekenen de
     * actuele stand, dus er hoeft geen nieuw optie-object te komen als er iets verandert.
     */
    protected readonly barOpties: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false, //hoeft niet, als het maar past
        //Zonder animatie. Een gestapelde balk met tientallen series over tientallen modellen is
        //al gauw meer dan duizend elementen; die een seconde lang laten inlopen betekent zestig
        //volledige hertekeningen per verversing, en dat is precies waar de browser over klaagt.
        animation: false,
        elements: {
            bar: {borderWidth: 0}
        },
        plugins: {
            legend: {display: false},
            //de tooltip toont vanzelf `dataset.label: waarde`, en dat label is de serienaam.
            //Zonder animatie: Chart.js schuift hem standaard in 400 ms naar zijn nieuwe plek, en
            //met veel smalle kolommen loopt hij daarmee zichtbaar achter de muis aan
            tooltip: {mode: 'nearest', intersect: false, animation: false}
        },
        scales: {
            x: {
                type: 'category',
                stacked: true,
                ticks: {
                    padding: 5,
                    color: context => this.isLiveKolom(context.index) ? 'red' : LABELKLEUR,
                    font: context => ({size: 10, weight: this.isLiveKolom(context.index) ? 'bold' : 'normal'})
                }
            },
            acties: {
                type: 'linear',
                position: 'left',
                display: 'auto',
                stacked: true,
                weight: 4,
                min: 0,
                title: {display: true, text: 'Actie'},
                ticks: {precision: 0} //geen decimalen
            },
            elementen: {
                type: 'linear',
                position: 'left',
                display: 'auto',
                stacked: true,
                weight: 3,
                min: 0,
                grid: {color: 'rgba(0,255,0,0.1)'},
                title: {display: true, text: 'Type', color: '#348534'}, //passend bij de kleuren
                ticks: {precision: 0}
            },
            norm: {
                type: 'linear',
                position: 'left',
                display: 'auto',
                stacked: true,
                weight: 2,
                min: 0,
                grid: {color: 'rgba(255,0,0,0.1)'},
                title: {display: true, text: 'Norm', color: '#F00'},
                ticks: {precision: 0}
            }
        }
    };

    /**
     * De opties van de donut.
     */
    protected readonly donutOpties: ChartOptions<'doughnut'> = {
        responsive: true,
        //De donut krijgt zijn maat van zijn vak (dat is in de SCSS vierkant gehouden). Met
        //`maintainAspectRatio` zou Chart.js zelf de hoogte van de canvas zetten, terwijl de
        //hoogte van dat vak juist uit de canvas volgt — dan blijven de resize-waarnemer en de
        //layout elkaar aanjagen.
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            legend: {display: false},
            tooltip: {animation: false}
        }
    };

    constructor() {
        super();
        //het paneel begint bij een volgende opening weer in het overzicht, net als in ng1, waar
        //sluiten via setDetail(null) liep
        effect(() => {
            if (!this.dashboard.open()) {
                this.detail.set(null);
            }
        });
    }

    /**
     * Het eiland verdwijnt (state-wissel, uitloggen): dan hoeft het backend ons ook niets meer te
     * sturen. De service is een root-singleton en zou anders geabonneerd blijven.
     */
    ngOnDestroy(): void {
        this.dashboard.sluiten();
    }

    /**
     * Sluit het paneel.
     */
    protected sluitPaneel(): void {
        this.dashboard.sluiten();
    }

    /**
     * Wisselt tussen alleen de modellen die openstaan en alle modellen.
     */
    protected zetStand(stand: 'live' | 'alles'): void {
        this.stand.set(stand);
    }

    /**
     * Zet een hele balk (en zijn y-as) aan of uit.
     */
    protected toggleBalk(balk: BalkKey): void {
        this.balkAan.update(stand => ({...stand, [balk]: !stand[balk]}));
    }

    /**
     * Zet een serie aan of uit. Series die deze volgen gaan mee, zodat een normcategorie met
     * één knop compleet verschijnt of verdwijnt.
     */
    protected toggleSerie(serie: Serie): void {
        const verborgen = new Set(this.verborgen());
        const wordtVerborgen = !verborgen.has(serie.seriekey);
        for (const key of [serie.seriekey, ...SERIES.filter(s => s.volgt === serie.seriekey).map(s => s.seriekey)]) {
            if (wordtVerborgen) {
                verborgen.add(key);
            } else {
                verborgen.delete(key);
            }
        }
        this.verborgen.set(verborgen);
    }

    /**
     * De randkleur van het knopje: die van de balk waar de serie in valt.
     */
    protected randkleurVan(serie: Serie): string {
        return RANDKLEUR[serie.balk];
    }

    /**
     * Of een serie op dit moment getoond wordt: zijn balk staat aan en hij is niet uitgezet.
     */
    protected isZichtbaar(serie: Serie): boolean {
        return this.balkAan()[serie.balk] && !this.verborgen().has(serie.seriekey);
    }

    /**
     * De gebruiker klikt op een kolom of op het label eronder: dat model in detail bekijken.
     * In detailmodus doet een klik niets — daar is maar één kolom.
     */
    protected kiesKolom(kolom: number): void {
        if (this.detail()) {
            return;
        }
        const modelId = this.telling().chartmodels[kolom];
        if (modelId) {
            this.detail.set(modelId);
        }
    }

    /**
     * Bladert naar het vorige of volgende model, rond aan het eind.
     */
    protected zapDetail(richting: number): void {
        const models = this.telling().models;
        const huidig = models.indexOf(this.detail() ?? '');
        if (huidig === -1) {
            this.detail.set(null); //het model is verdwenen uit de telling
            return;
        }
        this.detail.set(models[(huidig + richting + models.length) % models.length]);
    }

    /**
     * Bladert door naar het eerstvolgende model van een ándere gebruiker.
     */
    protected zapDetailUser(richting: number): void {
        const gegevens = this.dashboard.gegevens();
        const models = this.telling().models;
        const huidig = models.indexOf(this.detail() ?? '');
        if (huidig === -1) {
            this.detail.set(null);
            return;
        }
        const user = gegevens.modelinfo[models[huidig]]?.user;
        let index = huidig;
        do {
            index = (index + richting + models.length) % models.length;
            const info = gegevens.modelinfo[models[index]];
            if (info && info.user !== user) {
                this.detail.set(models[index]);
                return;
            }
        } while (index !== huidig);
    }

    /**
     * Sluit het detailoverzicht en keert terug naar het overzicht.
     */
    protected sluitDetail(): void {
        this.detail.set(null);
    }

    /**
     * Open het model dat in detail getoond wordt: meekijken met dat van een ander, of gewoon
     * openen als het van de gebruiker zelf is. Het modelbeheer laadt het model en sluit ons.
     */
    protected meekijken(): void {
        const modelId = this.detail();
        if (this.sessie.magMeekijken() && modelId) {
            void this.modelbeheer.meekijken(modelId, this.isEigenModel());
        }
    }

    /**
     * Of het label van deze kolom bij een model hoort dat op dit moment openstaat. In de stand
     * "Live" zijn dat ze allemaal en markeren we niets — precies zoals de oude Chart.js-plugin.
     */
    private isLiveKolom(kolom: number): boolean {
        if (this.stand() === 'live') {
            return false;
        }
        const modelId = this.telling().chartmodels[kolom];
        return !!modelId && this.dashboard.gegevens().live.includes(modelId);
    }
}
